<template>
  <PageWrap
    wide
    :heading="$t('admin.nav.links.services')"
    :loading="!allDataLoaded || getServiceGroupsLoading"
  >
    <template #actions>
      <BaseButton
        :routerLink="{ name: 'admin-service-update-prices' }"
        color="inverted"
        v-highlight="'update-service-prices'"
        v-intercom="'update-service-prices'"
        v-test="'btn-update-prices'"
      >
        {{ $t('products.update_prices') }}
      </BaseButton>
      <BaseButton
        :options="addNewOptions"
        v-test="'btn-add-new'"
      >
        {{ $t('global.actions.create_new') }}
      </BaseButton>
    </template>
    <Flex direction="column">
      <ScheduledPriceUpdate />
      <PageHeader v-if="!sortReorder">
        <template #left>
          <BaseSwitch
            v-if="servicePackagesEnabled"
            v-model="activeTab"
            :options="[
              {
                label: t('global.items.service', 1),
                value: 'services'
              },
              {
                label: t('global.items.package', 1),
                value: 'packages',
                info: { label: t('global.new'), color: 'green' }
              }
            ]"
            v-test="'services-packages-switch'"
          />
          <template v-if="activeTab === 'services'">
            <BaseDropdown
              v-model="filterValue"
              :options="[
                {
                  value: 'all',
                  label: $t('services.filter.all')
                },
                {
                  value: 'bookable',
                  label: $t('services.filter.bookable_online')
                },
                {
                  value: 'not_bookable',
                  label: $t('services.filter.not_bookable_online')
                }
              ]"
              v-test="'service-filter'"
            />
            <BaseSearch
              v-model="searchQuery"
              v-test="'service-search'"
            />
          </template>
        </template>
        <template #right>
          <BaseButton
            icon="swap-vertical"
            color="inverted"
            @click="sortReorder = true"
            v-test="'btn-sort-all'"
          />
        </template>
      </PageHeader>
      <BaseAlert
        v-if="sortReorder"
        icon="flip"
        :text="$t('services.reorder_services')"
        :primaryAction="$t('global.done')"
        @primaryAction="sortReorder = false"
      />
      <template v-if="activeTab === 'services'">
        <BaseCard>
          <Flex
            direction="column"
            :gap="0.5"
          >
            <List
              v-if="allDataLoaded && !getServiceGroupsLoading"
              :data="filteredCategoriesWithServices"
              :activeId="activeId"
              @toggle="(id) => (activeId = activeId === id ? null : id)"
            />
            <CreateButton
              toRouteName="admin-service-category-new"
              :text="$t('global.actions.create_category')"
              v-test="'service-category-create-list-bottom'"
            />
          </Flex>
        </BaseCard>
      </template>
      <BaseCard v-else>
        <PackageList />
      </BaseCard>
    </Flex>
  </PageWrap>
</template>

<script
  lang="ts"
  setup
>
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/modules/PageHeader.vue';
import { useServiceGroupsStore } from '@/stores/service-groups';
import { useSessionStore } from '@/stores/session';
import unleash from '@/unleash';
import PageWrap from '../PageWrap.vue';
import List from './list/index.vue';
import PackageList from './package/List.vue';
import ScheduledPriceUpdate from './price-update/scheduled-price-update/index.vue';
import CreateButton from './shared/CreateButton.vue';

const { t } = useI18n();

const servicePackagesEnabled = computed(() =>
  unleash.isEnabled('ServicePackages'),
);

const addNewOptions = computed(() => [
  {
    label: t('services.add_category'),
    routerLink: {
      name: 'admin-service-category-new',
    },
  },
  {
    label: t('services.add_service'),
    routerLink: {
      name: 'admin-service-new',
    },
  },
  ...(servicePackagesEnabled.value
    ? [
        {
          label: t('services.add_package'),
          routerLink: {
            name: 'admin-service-package-new',
          },
        },
      ]
    : []),
]);

const { allDataLoaded } = storeToRefs(useSessionStore());

const { categoriesWithServices, getServiceGroupsLoading } = storeToRefs(
  useServiceGroupsStore(),
);

type BookableFilter = 'all' | 'bookable' | 'not_bookable';

const filterValue = ref<BookableFilter>('all');
const searchQuery = ref('');
const activeId = ref(null);
const sortReorder = ref(false);

type ServicesTab = 'services' | 'packages';

const activeTab = ref<ServicesTab>('services');

provide('services-category-sort-order', sortReorder);
provide('services-active-tab', activeTab);

const filterByNameOrDescription = (
  item: { name: string; description?: string | null },
  searchQuery: string,
) =>
  !!(
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

const filterByOnlineBookable = (item: { services: { bookable: boolean }[] }) =>
  filterValue.value === 'all'
    ? true
    : !!(filterValue.value === 'bookable'
        ? item.services[0].bookable
        : !item.services[0].bookable);

const filteredCategoriesWithServices = computed(() =>
  categoriesWithServices.value
    .map((category) => ({
      ...category,
      services: category.services.filter(
        (service) =>
          filterByOnlineBookable(service) &&
          (filterByNameOrDescription(service, searchQuery.value) ||
            filterByNameOrDescription(category, searchQuery.value)),
      ),
    }))
    .filter((category) =>
      searchQuery.value !== ''
        ? category.services.length
        : category.services.length ||
          filterByNameOrDescription(category, searchQuery.value),
    ),
);
</script>
